import type { Expr, Stmt } from '../ir.js';
import { CodeWriter } from '../writer.js';
import type { Code, GenContext } from './context.js';
import {
  ATOM,
  joinParts,
  MISSING,
  numberLiteral,
  sliceEnd,
  stringLiteral,
  wrap,
} from './context.js';

const PY_ZERO: Record<string, string> = {
  int: '0',
  double: '0.0',
  string: "''",
  bool: 'False',
};

const P = { or: 1, and: 2, not: 3, cmp: 4, add: 5, mul: 6, unary: 7 };

/**
 * A text literal inside an f-string's `{…}` takes single quotes: the same quote as the f-string
 * itself only works from Python 3.12.
 */
function singleQuoted(s: string): string {
  const body = JSON.stringify(s).slice(1, -1).replace(/\\"/g, '"').replace(/'/g, "\\'");
  return `'${body}'`;
}

function fStringPart(s: string): string {
  return JSON.stringify(s).slice(1, -1).replace(/\{/g, '{{').replace(/\}/g, '}}');
}

/**
 * `b + 1` for range(), folded where it reads better: `to 10` gives `range(1, 11)` and
 * `to n - 1` gives `range(2, n)`.
 */
function offset(c: Code, e: Expr, by: number, expr: (x: Expr) => Code): string {
  if (e.k === 'num' && Number.isInteger(e.value)) return numberLiteral(e.value + by);
  if (e.k === 'arith' && (e.op === '+' || e.op === '-') && e.right.k === 'num') {
    const k = (e.op === '+' ? e.right.value : -e.right.value) + by;
    if (k === 0) return wrap(expr(e.left), P.add);
    return `${wrap(expr(e.left), P.add)} ${k > 0 ? '+' : '-'} ${numberLiteral(Math.abs(k))}`;
  }
  return `${wrap(c, P.add)} ${by > 0 ? '+' : '-'} ${Math.abs(by)}`;
}

export function generatePy(ctx: GenContext): CodeWriter {
  const w = new CodeWriter('    ');
  /** Inside an f-string hole, where a literal must not reuse the f-string's double quote. */
  let inHole = false;

  const expr = (e: Expr): Code => {
    switch (e.k) {
      case 'num':
        return { code: numberLiteral(e.value), prec: e.value < 0 ? P.unary : ATOM };
      case 'text':
        return { code: inHole ? singleQuoted(e.value) : stringLiteral(e.value), prec: ATOM };
      case 'bool':
        return { code: e.value ? 'True' : 'False', prec: ATOM };
      case 'var':
        return { code: ctx.id(e.name), prec: ATOM };
      case 'read':
        return {
          code: e.as === 'int' ? 'int(input())' : e.as === 'double' ? 'float(input())' : 'input()',
          prec: ATOM,
        };
      case 'arith': {
        const op = e.op === 'idiv' ? '//' : e.op === 'mod' ? '%' : e.op;
        const prec = op === '+' || op === '-' ? P.add : P.mul;
        return {
          code: `${wrap(expr(e.left), prec)} ${op} ${wrap(expr(e.right), prec + 1)}`,
          prec,
        };
      }
      case 'compare':
        return {
          code: `${wrap(expr(e.left), P.add)} ${e.op} ${wrap(expr(e.right), P.add)}`,
          prec: P.cmp,
        };
      case 'logic': {
        const prec = e.op === 'and' ? P.and : P.or;
        return {
          // Nested and/or always get parentheses, as in the other views.
          code: `${wrap(expr(e.left), P.and + 1)} ${e.op} ${wrap(expr(e.right), P.and + 1)}`,
          prec,
        };
      }
      case 'not':
        return { code: `not ${wrap(expr(e.arg), P.add)}`, prec: P.not };
      case 'join': {
        const outer = inHole;
        inHole = true;
        const body = joinParts(e)
          .map((part) => (part.k === 'text' ? fStringPart(part.value) : `{${expr(part).code}}`))
          .join('');
        inHole = outer;
        return { code: `f"${body}"`, prec: ATOM };
      }
      case 'length':
        return { code: `len(${expr(e.arg).code})`, prec: ATOM };
      case 'code':
        return { code: `ord(${expr(e.arg).code})`, prec: ATOM };
      case 'chr':
        return { code: `chr(${expr(e.arg).code})`, prec: ATOM };
      case 'case':
        return { code: `${wrap(expr(e.arg), ATOM)}.${e.upper ? 'upper' : 'lower'}()`, prec: ATOM };
      case 'find':
        return { code: `${wrap(expr(e.hay), ATOM)}.find(${expr(e.needle).code})`, prec: ATOM };
      case 'substr': {
        // A slice says where the piece stops; `s[:k]` when it starts at the beginning.
        const text = wrap(expr(e.arg), ATOM);
        if (e.from.k === 'num' && e.from.value === 0) {
          return { code: `${text}[:${expr(e.len).code}]`, prec: ATOM };
        }
        const end = sliceEnd(e.from, e.len, expr, P.add);
        return { code: `${text}[${expr(e.from).code}:${end}]`, prec: ATOM };
      }
      case 'at': {
        const idx = `[${expr(e.index).code}]${e.index2 ? `[${expr(e.index2).code}]` : ''}`;
        return { code: `${ctx.id(e.name)}${idx}`, prec: ATOM };
      }
      case 'size':
        return {
          code: e.dim === 2 ? `len(${ctx.id(e.name)}[0])` : `len(${ctx.id(e.name)})`,
          prec: ATOM,
        };
      case 'missing':
        return { code: MISSING, prec: ATOM };
    }
  };

  const e = (x: Expr) => expr(x).code;

  const block = (body: readonly Stmt[]) =>
    w.indented(() => {
      if (body.length === 0) w.line('pass');
      else body.forEach(stmt);
    });

  const stmt = (s: Stmt): void => {
    switch (s.k) {
      case 'set':
        w.stmt(s, () => w.line(`${ctx.id(s.name)} = ${e(s.value)}`));
        break;
      case 'newArray': {
        const zero = PY_ZERO[s.elem];
        // One extra place when the student numbers from 1, so `a[n]` is really there.
        const size = (d: Expr) => (s.base === 1 ? `${wrap(expr(d), P.add)} + 1` : e(d));
        const [rows, cols] = s.dims;
        const text = cols
          ? `${ctx.id(s.name)} = [[${zero}] * (${size(cols)}) for _ in range(${size(rows)})]`
          : `${ctx.id(s.name)} = [${zero}] * (${size(rows)})`;
        w.stmt(s, () => w.line(text));
        break;
      }
      case 'setAt': {
        const idx = `[${e(s.index)}]${s.index2 ? `[${e(s.index2)}]` : ''}`;
        w.stmt(s, () => w.line(`${ctx.id(s.name)}${idx} = ${e(s.value)}`));
        break;
      }
      case 'change': {
        const d = s.delta;
        const text =
          d.k === 'num' && d.value < 0
            ? `${ctx.id(s.name)} -= ${numberLiteral(-d.value)}`
            : `${ctx.id(s.name)} += ${e(d)}`;
        w.stmt(s, () => w.line(text));
        break;
      }
      case 'print':
        w.stmt(s, () => {
          const end = s.end === 'newline' ? '' : s.end === 'space' ? ', end=" "' : ', end=""';
          w.line(`print(${e(s.value)}${end})`);
        });
        break;
      case 'if':
        w.stmt(
          s,
          () => w.line(`if ${e(s.branches[0]!.cond)}:`),
          () => {
            s.branches.forEach((b, i) => {
              if (i > 0) w.mapExpr(b.cond, w.line(`elif ${e(b.cond)}:`));
              block(b.body);
            });
            if (s.orElse) {
              w.line('else:');
              block(s.orElse);
            }
          },
        );
        break;
      case 'repeat':
        w.stmt(
          s,
          () => w.line(`for _ in range(${e(s.times)}):`),
          () => block(s.body),
        );
        break;
      case 'while':
        w.stmt(
          s,
          () => w.line(`while ${e(s.cond)}:`),
          () => block(s.body),
        );
        break;
      case 'for': {
        const to = expr(s.to);
        const range = s.down
          ? `range(${e(s.from)}, ${offset(to, s.to, -1, expr)}, -1)`
          : `range(${e(s.from)}, ${offset(to, s.to, 1, expr)})`;
        w.stmt(
          s,
          () => w.line(`for ${ctx.id(s.name)} in ${range}:`),
          () => block(s.body),
        );
        break;
      }
      case 'break':
        w.stmt(s, () => w.line('break'));
        break;
    }
  };

  ctx.program.body.forEach(stmt);
  w.mapAll(ctx.program.startId);
  return w;
}
